/**
 * Iter 243 · RFQ 2.0 detail-page sub-components
 *
 *   • ClientProposedLinesPanel — internal reviewer accepts/rejects
 *     items the client pushed via the public portal
 *   • ApprovalHistoryPanel — version-wise approval decisions
 *   • ShareLinkDialog — mint a magic link for the client portal
 */
import { useState } from "react";
import { CheckCircle2, XCircle, Copy, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";

export function ClientProposedLinesPanel({ proposed, onDecide }) {
  const [rejectingId, setRejectingId] = useState(null);
  const [note, setNote] = useState("");
  if (!proposed || proposed.length === 0) return null;
  return (
    <div className="border border-amber-300 bg-amber-50/40" data-testid="rfq-proposed-lines">
      <div className="px-3 py-2 border-b border-amber-300 bg-amber-100/60 text-xs font-semibold uppercase tracking-widest text-amber-800">
        Client-proposed lines ({proposed.length})
        <span className="ml-2 text-[10px] normal-case font-normal text-amber-700">
          Accepting a line will start a new revision automatically
        </span>
      </div>
      <div className="divide-y divide-amber-200">
        {proposed.map((p) => (
          <div key={p.id} className="p-3 flex items-start gap-3" data-testid={`rfq-proposed-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{p.name}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {p.qty} {p.unit || "unit"}
                {p.proposer_name && ` · by ${p.proposer_name}`}
                {p.created_at && ` · ${fmtRelative(p.created_at)}`}
              </div>
              {p.note && <div className="text-xs text-muted-foreground italic mt-1">&ldquo;{p.note}&rdquo;</div>}
              {rejectingId === p.id && (
                <div className="mt-2 flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)}
                          placeholder="Reason (optional)"
                          className="flex-1 text-xs px-2 py-1 border border-border bg-background"
                          data-testid={`rfq-proposed-reject-note-${p.id}`} />
                  <button type="button"
                           onClick={() => { onDecide(p.id, "reject", note); setRejectingId(null); setNote(""); }}
                           className="text-xs px-2 py-1 bg-rose-600 text-white font-medium"
                           data-testid={`rfq-proposed-reject-confirm-${p.id}`}>
                    Confirm dismiss
                  </button>
                  <button type="button" onClick={() => { setRejectingId(null); setNote(""); }}
                           className="text-xs px-2 py-1 border border-border">
                    Cancel
                  </button>
                </div>
              )}
            </div>
            {rejectingId !== p.id && (
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => onDecide(p.id, "accept")}
                         className="text-[11px] px-2 py-1 bg-emerald-600 text-white hover:opacity-90 flex items-center gap-1 font-medium"
                         data-testid={`rfq-proposed-accept-${p.id}`}>
                  <CheckCircle2 className="w-3 h-3" /> Accept
                </button>
                <button type="button" onClick={() => setRejectingId(p.id)}
                         className="text-[11px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                         data-testid={`rfq-proposed-reject-${p.id}`}>
                  <XCircle className="w-3 h-3" /> Dismiss
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ApprovalHistoryPanel({ rows = [], currentRev = 1 }) {
  // Group by revision (desc)
  const byRev = {};
  for (const r of rows) {
    const k = r.revision_no || 1;
    (byRev[k] ||= []).push(r);
  }
  const revs = Object.keys(byRev).map(Number).sort((a, b) => b - a);
  return (
    <div className="border border-border" data-testid="rfq-approval-history">
      <div className="px-3 py-2 border-b border-border bg-muted/40 text-xs font-semibold uppercase tracking-widest">
        Approval history
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-xs text-muted-foreground">
          No approval activity yet. Once someone approves, rejects, or requests
          revision, a versioned trail appears here.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {revs.map((rev) => (
            <div key={rev} className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                Revision {rev}
                {rev === currentRev && (
                  <span className="text-[9px] px-1.5 py-0.5 bg-primary text-primary-foreground uppercase tracking-widest">Current</span>
                )}
              </div>
              <div className="space-y-2">
                {byRev[rev].map((h) => (
                  <div key={h.id} className="text-xs flex items-start gap-2" data-testid={`rfq-history-${h.id}`}>
                    <span className={`shrink-0 mt-0.5 text-[9px] px-1.5 py-0.5 border uppercase tracking-widest ${
                      h.decision === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                      : h.decision === "rejected" ? "bg-rose-50 text-rose-700 border-rose-300"
                      : h.decision === "request_revision" ? "bg-amber-50 text-amber-700 border-amber-300"
                      : "bg-slate-50 text-slate-700 border-slate-300"}`}>
                      {(h.decision || "").replace(/_/g, " ")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div>
                        <b>{h.actor_name || "—"}</b>
                        <span className="text-muted-foreground text-[10px]"> · {h.kind} · {fmtRelative(h.at)}</span>
                        {h.recorded_by_name && h.recorded_by_name !== h.actor_name && (
                          <span className="text-muted-foreground text-[10px]"> · recorded by {h.recorded_by_name}</span>
                        )}
                      </div>
                      {h.note && <div className="text-muted-foreground italic mt-0.5">&ldquo;{h.note}&rdquo;</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ShareLinkDialog({ rid, rfq, onClose }) {
  const [linkInfo, setLinkInfo] = useState(null);
  const [ttl, setTtl] = useState(168);
  const [busy, setBusy] = useState(false);
  const wsId = rfq?.workspace_id;

  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/rfqs/${rid}/share-link`,
        {}, { params: { ttl_hours: ttl } });
      setLinkInfo(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };

  const fullUrl = linkInfo ? `${window.location.origin}${linkInfo.url}` : "";
  const copy = () => {
    navigator.clipboard.writeText(fullUrl);
    toast.success("Link copied");
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-background border border-border shadow-2xl"
            data-testid="rfq-share-dialog">
        <div className="px-4 py-3 border-b border-border font-semibold text-sm flex items-center gap-2">
          <Link2 className="w-4 h-4" /> Share with client
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            Mint a passwordless magic link so <b>{rfq?.client_requestor_name || rfq?.client_name || "the client"}</b> can
            review, add items, comment, and approve or request revision — all
            without needing a login.
          </p>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Link expiry</label>
            <select value={ttl} onChange={(e) => setTtl(Number(e.target.value))}
                     className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border"
                     data-testid="rfq-share-ttl">
              <option value={24}>24 hours</option>
              <option value={72}>3 days</option>
              <option value={168}>7 days (recommended)</option>
              <option value={720}>30 days</option>
            </select>
          </div>
          {!linkInfo ? (
            <button type="button" onClick={issue} disabled={busy}
                     className="w-full text-xs px-3 py-2 bg-primary text-primary-foreground disabled:opacity-50 font-medium flex items-center justify-center gap-1.5"
                     data-testid="rfq-share-generate">
              <Link2 className="w-3.5 h-3.5" /> {busy ? "Generating…" : "Generate secure link"}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 border border-border p-2 bg-muted/40">
                <input readOnly value={fullUrl}
                        className="flex-1 text-xs bg-transparent border-0 focus:outline-none tabular-nums"
                        data-testid="rfq-share-url" />
                <button type="button" onClick={copy}
                         className="text-[11px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                         data-testid="rfq-share-copy">
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <a href={fullUrl} target="_blank" rel="noreferrer"
                    className="text-[11px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                    data-testid="rfq-share-preview">
                  <ExternalLink className="w-3 h-3" /> Preview
                </a>
              </div>
              <div className="text-[10px] text-muted-foreground">
                Expires: {linkInfo.expires_at ? new Date(linkInfo.expires_at).toLocaleString() : "—"}
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end">
          <button type="button" onClick={onClose}
                   className="text-xs px-3 py-1.5 border border-border hover:bg-muted"
                   data-testid="rfq-share-close">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// end RfqPanels
